/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import loadable from '@loadable/component'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue, getFallbackSiteId} from './utils/site-config'

// Components
import {Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui'
import {configureRoutes} from '@salesforce/retail-react-app/app/utils/routes-utils'
import {routes as _routes} from '@salesforce/retail-react-app/app/routes'

const fallback = <Skeleton height="75vh" width="100%" />

// Create your pages here and add them to the routes array
// Use loadable to split code into smaller js chunks
const Home = loadable(() => import('./pages/home'), {fallback})
const Preview = loadable(() => import('./pages/preview'), {fallback})
const Cart = loadable(() => import('./pages/cart'), {fallback})
const Checkout = loadable(() => import('./pages/checkout'), {
    fallback
})
const Confirmation = loadable(() => import('./pages/checkout/confirmation'), {
    fallback
})
const ProductList = loadable(() => import('./pages/product-list'), {
    fallback
})
const ProductDetail = loadable(() => import('./pages/product-detail'), {fallback})
const SSOCallback = loadable(() => import('./pages/sso-callback'))
const SessionRedirect = loadable(() => import('./pages/session-redirect'))
const AdyenDropIn = loadable(() => import('./integrations/adyen/components/dropin'))
const Auth = loadable(() => import('./pages/auth'))
const EmailConfirm = loadable(() => import('./pages/email-confirm'))
const Account = loadable(() => import('./pages/account'), {fallback})
const ResetPassword = loadable(() => import('./pages/reset-password'), {fallback})
const MyRewards = loadable(() => import('./pages/my-rewards/index'), {fallback})
const StoreLocator = loadable(() => import('./pages/store-locator/index'), {fallback})
const PrivacyPolicy = loadable(() => import('./pages/privacy-policy/index.jsx'), {fallback})
const ContentPage = loadable(() => import('./pages/content-page'), {fallback})
const ShareWishlist = loadable(() => import('./pages/share-wishlist/index.jsx'), {fallback})
const Sitemap = loadable(() => import('./pages/sitemap/index.jsx'), {fallback})
const DesignSystem = loadable(() => import('./pages/designsystem'), {fallback})
const DesignSystemSpacing = loadable(() => import('./pages/designsystem/spacing'), {fallback})
const DesignSystemButtons = loadable(() => import('./pages/designsystem/buttons'), {fallback})
const DesignSystemColors = loadable(() => import('./pages/designsystem/colors'), {fallback})
const DesignSystemComponents = loadable(() => import('./pages/designsystem/components'), {fallback})
const DesignSystemIcons = loadable(() => import('./pages/designsystem/icons'), {fallback})
const DesignSystemCmsDirectives = loadable(
    () => import('./pages/designsystem/cms-directives'),
    {fallback}
)
const DesignSystemCmsDynamicTags = loadable(
    () => import('./pages/designsystem/cms-dynamic-tags'),
    {fallback}
)

const PageNotFound = loadable(() => import('./pages/page-not-found'))

const routes = [
    {
        path: '/',
        component: Home,
        exact: true
    },
    {
        path: '/:pageName.html',
        component: ContentPage,
        exact: true
    },
    {
        path: '/ui/CMS/Content/*',
        component: Preview
    },
    {
        path: '/cart',
        component: Cart,
        exact: true
    },
    {
        path: '/p/:productName/:productId.html',
        component: ProductDetail
    },
    {
        path: '/checkout',
        component: Checkout,
        exact: true
    },
    {
        path: '/checkout/confirmation/:orderNo',
        component: Confirmation,
        exact: true
    },
    {
        path: '/search',
        component: ProductList
    },
    {
        path: '/:brandId([g|d]{1})/:categoryPath(.*)/',
        component: ProductList
    },
    {
        path: '/sso-callback',
        component: SSOCallback
    },
    {
        path: '/session-redirect',
        component: SessionRedirect
    },
    {
        path: '/adyen',
        component: AdyenDropIn
    },
    {
        path: '/login',
        component: Auth,
        exact: true
    },
    {
        path: '/registration',
        component: Auth,
        exact: true
    },
    {
        path: '/email-confirm',
        component: EmailConfirm,
        exact: true
    },
    {
        path: '/reset-password',
        component: ResetPassword,
        exact: true
    },
    {
        path: '/privacy-policy',
        component: PrivacyPolicy,
        exact: true
    },
    {
        path: '/account',
        component: Account
    },
    {
        path: '/my-rewards',
        component: MyRewards
    },
    {
        path: '/share-wishlist/:wishlistId',
        component: ShareWishlist,
        exact: true
    },
    {
        path: '/sitemap',
        component: Sitemap,
        exact: true
    },
    {
        path: '/designsystem',
        component: DesignSystem,
        exact: true
    },
    {
        path: '/designsystem/spacing',
        component: DesignSystemSpacing,
        exact: true
    },
    {
        path: '/designsystem/buttons',
        component: DesignSystemButtons,
        exact: true
    },
    {
        path: '/designsystem/colors',
        component: DesignSystemColors,
        exact: true
    },
    {
        path: '/designsystem/components',
        component: DesignSystemComponents,
        exact: true
    },
    {
        path: '/designsystem/icons',
        component: DesignSystemIcons,
        exact: true
    },
    {
        path: '/designsystem/cms-directives',
        component: DesignSystemCmsDirectives,
        exact: true
    },
    {
        path: '/designsystem/cms-dynamic-tags',
        component: DesignSystemCmsDynamicTags,
        exact: true
    }
]

export default () => {
    const config = getConfig()
    const siteId = getFallbackSiteId(config.app)

    if (getSiteEnvVarValue(config.app, 'STORELOCATOR_ENABLED', siteId) === 'true') {
        routes.push({
            path: '/stores',
            component: StoreLocator
        })
    }

    /* Must be the last one */
    routes.push({
        path: '*',
        component: PageNotFound
    })

    routes.push(..._routes)

    return configureRoutes(routes, config, {
        ignoredRoutes: ['/callback', '*']
    })
}
